import * as React from 'react';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useImpersonation } from '../context/ImpersonationContext';
import { logger } from '../utils/logger';
import '../styles/ChatbotPanel.css';
import useAdaptiveChatScroll from './hooks/useAdaptiveChatScroll';
import { useChat, ToolCall as SSEToolCall, StreamSegment, GeneratedContract, AttachedFileInfo } from './hooks/useChat';
import { useFileAttachments, AttachedFile } from './hooks/useFileAttachments';
import * as api from '../services/apiClient';

const TYPING_SPEED = 1; // Vitesse d'ecriture en millisecondes par caractere

// Fonction pour detecter les documents dans la reponse (web version - logging only)
const detectDocumentInResponse = (text: string) => {
  try {
    const startPattern = 'DOCUMENT_TO_OPEN_START';
    const endPattern = 'DOCUMENT_TO_OPEN_END';

    const startIndex = text.indexOf(startPattern);
    const endIndex = text.indexOf(endPattern);

    if (startIndex !== -1 && endIndex !== -1 && endIndex > startIndex) {
      const jsonStart = startIndex + startPattern.length;
      const jsonText = text.substring(jsonStart, endIndex).trim();

      try {
        const documentInfo = JSON.parse(jsonText);
        logger.log('Document reference detected:', documentInfo);
        // In web version, we don't open local files
        // The document reference is logged for potential future use
      } catch (parseError) {
        logger.error('Erreur lors du parsing JSON:', parseError);
      }
    }
  } catch (error) {
    logger.error('Erreur dans detectDocumentInResponse:', error);
  }
};

// Composant pour formater le texte Markdown
const MarkdownText: React.FC<{ text: string }> = ({ text }) => {
  const { setInitialClauseId, setInitialContractId, setActivePage } = useAppContext();

  const handleInternalLink = React.useCallback((url: string) => {
    const clauseMatch = url.match(/^\/clause\/(\d+)$/);
    if (clauseMatch) {
      setInitialClauseId(Number(clauseMatch[1]));
      setActivePage('page3');
      return;
    }
    const contractMatch = url.match(/^\/contract\/(\d+)$/);
    if (contractMatch) {
      setInitialContractId(Number(contractMatch[1]));
      setActivePage('page4');
      return;
    }
  }, [setInitialClauseId, setInitialContractId, setActivePage]);

  // Formater le texte inline (gras, italique, code inline, liens)
  const formatInlineText = (inputText: string, keyPrefix: string = ''): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    let remaining = inputText;
    let keyIndex = 0;

    while (remaining.length > 0) {
      // Code inline `code`
      const codeMatch = remaining.match(/^`([^`]+)`/);
      if (codeMatch) {
        parts.push(
          <code key={`${keyPrefix}-code-${keyIndex++}`} className="md-inline-code">
            {codeMatch[1]}
          </code>
        );
        remaining = remaining.substring(codeMatch[0].length);
        continue;
      }

      // Lien markdown [texte](url) - supporte aussi [[1]](url)
      const linkMatch = remaining.match(/^\[(.*?)\]\(([^)]+)\)/);
      if (linkMatch && linkMatch[1]) {
        const url = linkMatch[2];
        const isInternalLink = /^\/(clause|contract)\/\d+$/.test(url);
        if (isInternalLink) {
          parts.push(
            <span
              key={`${keyPrefix}-link-${keyIndex++}`}
              className="md-link md-internal-link"
              role="button"
              tabIndex={0}
              onClick={() => handleInternalLink(url)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleInternalLink(url);
                }
              }}
            >
              {linkMatch[1]}
            </span>
          );
        } else {
          const isSafeUrl = /^(https?:|mailto:|tel:|\/)/i.test(url);
          parts.push(
            <a
              key={`${keyPrefix}-link-${keyIndex++}`}
              href={isSafeUrl ? url : '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="md-link"
            >
              {linkMatch[1]}
            </a>
          );
        }
        remaining = remaining.substring(linkMatch[0].length);
        continue;
      }

      // Gras **texte**
      const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
      if (boldMatch) {
        parts.push(<strong key={`${keyPrefix}-bold-${keyIndex++}`}>{boldMatch[1]}</strong>);
        remaining = remaining.substring(boldMatch[0].length);
        continue;
      }

      // Italique *texte* (mais pas les listes)
      const italicMatch = remaining.match(/^\*([^*]+)\*/);
      if (italicMatch && !remaining.match(/^\* /)) {
        parts.push(<em key={`${keyPrefix}-italic-${keyIndex++}`}>{italicMatch[1]}</em>);
        remaining = remaining.substring(italicMatch[0].length);
        continue;
      }

      // Caractere normal
      const nextSpecial = remaining.search(/[`*\[]/);
      if (nextSpecial === -1) {
        parts.push(remaining);
        break;
      } else if (nextSpecial === 0) {
        parts.push(remaining[0]);
        remaining = remaining.substring(1);
      } else {
        parts.push(remaining.substring(0, nextSpecial));
        remaining = remaining.substring(nextSpecial);
      }
    }

    return parts;
  };

  const formatText = (inputText: string): React.ReactNode[] => {
    const result: React.ReactNode[] = [];
    const lines = inputText.split('\n');
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      const trimmedLine = line.trim();

      // Bloc de code ```
      if (trimmedLine.startsWith('```')) {
        const language = trimmedLine.substring(3).trim();
        const codeLines: string[] = [];
        i++;
        while (i < lines.length && !lines[i].trim().startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        result.push(
          <pre key={`code-block-${i}`} className="md-code-block">
            <code className={language ? `language-${language}` : ''}>
              {codeLines.join('\n')}
            </code>
          </pre>
        );
        i++;
        continue;
      }

      // Citation > texte
      if (trimmedLine.startsWith('> ')) {
        const quoteContent = trimmedLine.substring(2);
        result.push(
          <blockquote key={`quote-${i}`} className="md-blockquote">
            {formatInlineText(quoteContent, `quote-${i}`)}
          </blockquote>
        );
        i++;
        continue;
      }

      // Titres # ## ### ####
      const headingMatch = trimmedLine.match(/^(#{1,4})\s+(.+)$/);
      if (headingMatch) {
        const level = headingMatch[1].length;
        const content = headingMatch[2];
        const HeadingTag = `h${level + 2}` as 'h3' | 'h4' | 'h5' | 'h6'; // h3, h4, h5, h6
        result.push(
          <HeadingTag key={`heading-${i}`} className={`md-heading md-h${level}`}>
            {formatInlineText(content, `heading-${i}`)}
          </HeadingTag>
        );
        i++;
        continue;
      }

      // Liste a puces * ou -
      if (trimmedLine.match(/^[\*\-]\s+/)) {
        const bulletContent = trimmedLine.replace(/^[\*\-]\s+/, '');
        result.push(
          <div key={`bullet-${i}`} className="md-list-item">
            {formatInlineText(bulletContent, `bullet-${i}`)}
          </div>
        );
        i++;
        continue;
      }

      // Tableau Markdown
      if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
        const tableLines: string[] = [];
        // Collecter toutes les lignes du tableau
        while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
          tableLines.push(lines[i].trim());
          i++;
        }

        if (tableLines.length >= 2) {
          // Parser les cellules d'une ligne
          const parseCells = (row: string): string[] => {
            return row
              .slice(1, -1) // Retirer les | au debut et a la fin
              .split('|')
              .map(cell => cell.trim());
          };

          // Premiere ligne = en-tetes
          const headers = parseCells(tableLines[0]);

          // Trouver l'index de la ligne separateur (contient ---)
          let separatorIndex = tableLines.findIndex(line => line.includes('---'));
          if (separatorIndex === -1) separatorIndex = 1;

          // Lignes de donnees (apres le separateur)
          const dataRows = tableLines.slice(separatorIndex + 1).map(parseCells);

          result.push(
            <table key={`table-${i}`} className="md-table">
              <thead>
                <tr>
                  {headers.map((header, hi) => (
                    <th key={`th-${hi}`}>{formatInlineText(header, `th-${i}-${hi}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataRows.map((row, ri) => (
                  <tr key={`tr-${ri}`}>
                    {row.map((cell, ci) => (
                      <td key={`td-${ci}`}>{formatInlineText(cell, `td-${i}-${ri}-${ci}`)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }
        continue;
      }

      // Ligne vide
      if (trimmedLine === '') {
        result.push(<br key={`br-${i}`} />);
        i++;
        continue;
      }

      // Paragraphe normal
      result.push(
        <div key={`para-${i}`}>
          {formatInlineText(line, `para-${i}`)}
        </div>
      );
      i++;
    }

    return result;
  };

  return <>{formatText(text)}</>;
};

interface ToolCall {
  name: string;
  args: string;
  status?: string;
  result?: string;
}

interface TextSegment {
  type: 'text' | 'tool' | 'finalAnswer';
  content?: string;
  tool?: ToolCall;
}

// Composant pour afficher la reflexion dans un dropdown collapsible
const ReflectionDropdown: React.FC<{
  text: string;
  isStreaming: boolean;
}> = ({ text, isStreaming }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  if (!text || text.trim().length === 0) {
    return null;
  }

  return (
    <div className="reflection-dropdown">
      <button
        className={`reflection-header ${isOpen ? 'open' : ''}`}
        onClick={toggleOpen}
        type="button"
      >
        <span className={`reflection-label ${isStreaming ? 'shimmer' : ''}`}>
          Reflexion{isStreaming ? '...' : ''}
        </span>
        <svg
          className={`reflection-chevron ${isOpen ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      <div
        ref={contentRef}
        className={`reflection-content ${isOpen ? 'open' : ''}`}
      >
        <div className="reflection-text">
          <MarkdownText text={text} />
        </div>
      </div>
    </div>
  );
};

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

// Contract download box component (Web version - uses browser download)
const ContractDownloadBox: React.FC<{
  contract: GeneratedContract;
}> = ({ contract }) => {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [isDownloaded, setIsDownloaded] = React.useState(false);

  const handleDownload = () => {
    setIsDownloading(true);
    try {
      api.downloadGeneratedContract(contract);
      setIsDownloaded(true);
    } catch (error) {
      logger.error('Download error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadAgain = () => {
    setIsDownloaded(false);
    handleDownload();
  };

  return (
    <div className="contract-download-box">
      <div className="contract-download-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 18V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M9 15L12 18L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className="contract-download-info">
        <div className="contract-download-filename">{contract.filename}</div>
        <div className="contract-download-size">{formatFileSize(contract.size_bytes)}</div>
      </div>
      <div className="contract-download-actions">
        {isDownloaded ? (
          <>
            <button
              className="contract-download-button success"
              disabled
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span>Téléchargé</span>
            </button>
            <button
              className="contract-download-again"
              onClick={handleDownloadAgain}
            >
              Télécharger a nouveau
            </button>
          </>
        ) : (
          <button
            className={`contract-download-button ${isDownloading ? 'downloading' : ''}`}
            onClick={handleDownload}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <>
                <svg className="svg-spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                <span>Chargement...</span>
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span>Télécharger</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const BotMessage: React.FC<{
  text: string;
  isLastMessage: boolean;
  isSending: boolean;
  isWelcomeMessage?: boolean;
  toolCalls?: SSEToolCall[];
  streamSegments?: StreamSegment[];
  isWaitingFirstEvent?: boolean;
  thinkingMessage?: string | null;
  generatedContract?: GeneratedContract | null;
}> = ({
  text,
  isLastMessage,
  isSending,
  isWelcomeMessage = false,
  toolCalls = [],
  streamSegments = [],
  isWaitingFirstEvent = false,
  thinkingMessage = null,
  generatedContract = null
}) => {
    const [displayedText, setDisplayedText] = React.useState('');
    const continueAnimating = React.useRef(false);

    // Fonction pour extraire les tool calls et creer des segments ordonnes
    const extractSegments = (inputText: string): { segments: TextSegment[]; toolCalls: ToolCall[]; cleanedText: string } => {
      // Retirer les "undefined" isoles du texte (envoyes par le backend)
      inputText = inputText.replace(/\bundefined\b/g, '');
      const toolDeclarationRegex = /\[Tool\]\[name:([^\]]*)\]\[args:([^\]]*)\]\[end\]/g;
      const toolResultRegex = /\[Tool result\]\[status:([^\]]*)\]\[result:([^\]]*)\]\[end\]/g;

      const toolCalls: ToolCall[] = [];
      const toolMatches: Array<{ start: number; end: number; toolIndex: number }> = [];
      const resultMatches: Array<{ start: number; end: number; status: string; result: string }> = [];

      // Extraire les declarations
      let match;
      while ((match = toolDeclarationRegex.exec(inputText)) !== null) {
        const toolIndex = toolCalls.length;
        const toolCall = {
          name: match[1],
          args: match[2]
        };
        toolCalls.push(toolCall);
        toolMatches.push({ start: match.index, end: match.index + match[0].length, toolIndex });
      }

      // Extraire les results avec indexOf pour plus de fiabilite
      const toolResultStart = '[Tool result][status:';
      const resultMarker = '][result:';
      const endMarker = '][end]';

      let searchStart = 0;
      while (true) {
        const startIdx = inputText.indexOf(toolResultStart, searchStart);
        if (startIdx === -1) break;

        const statusStart = startIdx + toolResultStart.length;
        const statusEnd = inputText.indexOf(resultMarker, statusStart);
        if (statusEnd === -1) break;

        const resultStart = statusEnd + resultMarker.length;
        const resultEnd = inputText.indexOf(endMarker, resultStart);
        if (resultEnd === -1) break;

        const status = inputText.substring(statusStart, statusEnd);
        const resultContent = inputText.substring(resultStart, resultEnd);

        resultMatches.push({
          start: startIdx,
          end: resultEnd + endMarker.length,
          status: status,
          result: resultContent
        });

        searchStart = resultEnd + endMarker.length;
      }

      // Associer chaque result au tool correspondant
      for (let i = 0; i < toolMatches.length; i++) {
        const toolMatch = toolMatches[i];
        const nextToolStart = i < toolMatches.length - 1 ? toolMatches[i + 1].start : Infinity;

        const correspondingResult = resultMatches.find(r =>
          r.start >= toolMatch.end && r.start < nextToolStart
        );

        if (correspondingResult) {
          toolCalls[toolMatch.toolIndex].status = correspondingResult.status;
          toolCalls[toolMatch.toolIndex].result = correspondingResult.result;
        }
      }

      // Creer tous les matches a enlever (tools + results)
      const allMatches: Array<{ start: number; end: number; type: 'tool' | 'result'; toolIndex?: number }> = [
        ...toolMatches.map(m => ({ ...m, type: 'tool' as const })),
        ...resultMatches.map(m => ({ start: m.start, end: m.end, type: 'result' as const }))
      ];
      allMatches.sort((a, b) => a.start - b.start);

      // Creer les segments et le texte nettoye
      const segments: TextSegment[] = [];
      let cleanedText = '';
      let lastEnd = 0;

      for (const m of allMatches) {
        // Texte avant ce match
        let textBefore = inputText.substring(lastEnd, m.start);

        // Nettoyer les retours a la ligne
        if (m.type === 'tool') {
          textBefore = textBefore.replace(/\s+$/, '');
        }
        if (m.type === 'result') {
          textBefore = textBefore.replace(/\s+$/, '');
        }

        if (textBefore) {
          segments.push({ type: 'text', content: textBefore });
          cleanedText += textBefore;
        }

        // Ajouter le segment tool
        if (m.type === 'tool' && m.toolIndex !== undefined) {
          segments.push({ type: 'tool', tool: toolCalls[m.toolIndex] });
        }

        lastEnd = m.end;
      }

      // Texte restant apres le dernier match
      let remainingText = inputText.substring(lastEnd);
      if (allMatches.length > 0 && allMatches[allMatches.length - 1].type === 'result') {
        remainingText = remainingText.trimStart();
      }
      if (remainingText) {
        segments.push({ type: 'text', content: remainingText });
        cleanedText += remainingText;
      }

      return { segments, toolCalls, cleanedText };
    };

    // Extraire les segments du texte
    const { segments: rawSegments, toolCalls: parsedToolCalls, cleanedText: textWithoutTools } = extractSegments(text);

    // Traiter [to user] dans les segments
    const finalAnswerMarker = '[to user]';
    const processedSegments: TextSegment[] = [];
    let hasFinalAnswer = false;
    let cleanText = '';

    for (const segment of rawSegments) {
      if (segment.type === 'text' && segment.content) {
        const markerIndex = segment.content.indexOf(finalAnswerMarker);

        if (markerIndex !== -1 && !hasFinalAnswer) {
          hasFinalAnswer = true;

          let beforePart = segment.content.substring(0, markerIndex);
          beforePart = beforePart.replace(/\s+$/, '');

          if (beforePart) {
            processedSegments.push({ type: 'text', content: beforePart });
            cleanText += beforePart;
          }

          let afterPart = segment.content.substring(markerIndex + finalAnswerMarker.length);
          afterPart = afterPart.trimStart();

          if (afterPart) {
            processedSegments.push({ type: 'finalAnswer', content: afterPart });
            cleanText += afterPart;
          }
        } else if (hasFinalAnswer) {
          processedSegments.push({ type: 'finalAnswer', content: segment.content });
          cleanText += segment.content;
        } else {
          processedSegments.push(segment);
          cleanText += segment.content;
        }
      } else {
        processedSegments.push(segment);
      }
    }

    const segments = processedSegments;

    React.useEffect(() => {
      if (isLastMessage && isSending) {
        continueAnimating.current = true;
      }

      if (continueAnimating.current) {
        if (displayedText.length < cleanText.length) {
          const timeoutId = setTimeout(() => {
            setDisplayedText(cleanText.slice(0, displayedText.length + 1));
          }, TYPING_SPEED);

          return () => clearTimeout(timeoutId);
        } else {
          if (!isSending) {
            continueAnimating.current = false;
          }
          if (displayedText !== cleanText) {
            setDisplayedText(cleanText);
          }
        }
      } else {
        if (displayedText !== cleanText) {
          setDisplayedText(cleanText);
        }
      }
    }, [cleanText, displayedText, isLastMessage, isSending]);

    const hasStreamSegments = streamSegments && streamSegments.length > 0;

    // Helper to render a single SSE tool by ID
    const renderSSETool = (toolId: string, keyIndex: number) => {
      const tool = toolCalls.find(t => t.id === toolId);
      if (!tool) return null;

      const isRunning = tool.status === 'running';
      const isSuccess = tool.status === 'success';
      const isError = tool.status === 'error';

      return (
        <div key={`sse-tool-${keyIndex}`} className="tool-call-box">
          <div className="tool-call-header">
            <span className="tool-name"><MarkdownText text={tool.displayName || tool.name} /></span>
          </div>
          <div className="tool-call-result">
            {isRunning && <span className="tool-waiting">En attente du resultat...</span>}
            {isError && <span className="tool-error">L'outil n'a pas reussi</span>}
            {isSuccess && (
              <span className="tool-success">
                <MarkdownText text={tool.result || 'Termine'} />
              </span>
            )}
          </div>
        </div>
      );
    };

    // Helper to clean tool markers from text
    const cleanToolMarkers = (text: string): string => {
      let cleaned = text.replace(/\[Tool\]\[name:[^\]]*\]\[args:[^\]]*\]\[end\]/g, '');

      const toolResultStart = '[Tool result][status:';
      const endMarker = '][end]';
      let searchStart = 0;
      while (true) {
        const startIdx = cleaned.indexOf(toolResultStart, searchStart);
        if (startIdx === -1) break;

        const endIdx = cleaned.indexOf(endMarker, startIdx);
        if (endIdx === -1) break;

        cleaned = cleaned.substring(0, startIdx) + cleaned.substring(endIdx + endMarker.length);
        searchStart = startIdx;
      }

      cleaned = cleaned.replace(/\bundefined\b/g, '');
      cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();

      return cleaned;
    };

    // Helper to render text with [to user] detection
    const renderTextSegment = (textContent: string, keyIndex: number, isLastSegment: boolean) => {
      const result: React.ReactNode[] = [];

      textContent = cleanToolMarkers(textContent);
      if (!textContent.trim()) return result;

      const finalAnswerMarker = '[to user]';
      const markerIndex = textContent.indexOf(finalAnswerMarker);

      if (markerIndex !== -1) {
        const beforePart = textContent.substring(0, markerIndex).replace(/\s+$/, '');
        const afterPart = textContent.substring(markerIndex + finalAnswerMarker.length).trimStart();

        if (beforePart.trim().length > 0) {
          if (isWelcomeMessage) {
            result.push(<MarkdownText key={`text-before-${keyIndex}`} text={beforePart} />);
          } else {
            result.push(
              <ReflectionDropdown
                key={`reflection-${keyIndex}`}
                text={beforePart}
                isStreaming={false}
              />
            );
          }
        }

        if (afterPart.trim().length > 0) {
          result.push(
            <div key={`final-${keyIndex}`} className="final-answer-box">
              <div className="final-answer-content">
                <MarkdownText text={afterPart} />
              </div>
            </div>
          );
        }
      } else {
        if (textContent.trim().length > 0) {
          if (isWelcomeMessage) {
            result.push(<MarkdownText key={`text-${keyIndex}`} text={textContent} />);
          } else {
            const isStreamingThis = isLastMessage && isSending && isLastSegment;
            const isHistoricalMessage = !(isLastMessage && isSending);

            // Pour les messages historiques sans [to user], afficher directement le contenu
            if (isHistoricalMessage) {
              result.push(
                <div key={`final-${keyIndex}`} className="final-answer-box">
                  <div className="final-answer-content">
                    <MarkdownText text={textContent} />
                  </div>
                </div>
              );
            } else {
              // Message en streaming - utiliser ReflectionDropdown
              result.push(
                <ReflectionDropdown
                  key={`reflection-${keyIndex}`}
                  text={textContent}
                  isStreaming={isStreamingThis}
                />
              );
            }
          }
        }
      }

      return result;
    };

    // Render segments in chronological order
    const renderSegments = () => {
      const result: React.ReactNode[] = [];

      if (hasStreamSegments) {
        streamSegments.forEach((segment, index) => {
          const isLastSegment = index === streamSegments.length - 1;

          if (segment.type === 'text' && segment.text) {
            const textElements = renderTextSegment(segment.text, index, isLastSegment);
            result.push(...textElements);
          } else if (segment.type === 'tool' && segment.toolId) {
            const toolElement = renderSSETool(segment.toolId, index);
            if (toolElement) {
              result.push(toolElement);
            }
          }
        });

        return result;
      }

      // LEGACY: No stream segments - use original segment-based logic
      let textPosition = 0;
      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];

        if (segment.type === 'text' && segment.content) {
          const segmentLength = segment.content.length;
          const segmentEnd = textPosition + segmentLength;
          const hasToolOrFinalAfter = segments.slice(i + 1).some(s => s.type === 'tool' || s.type === 'finalAnswer');

          if (textPosition < displayedText.length || hasToolOrFinalAfter) {
            const visibleEnd = hasToolOrFinalAfter ? segmentEnd : Math.min(segmentEnd, displayedText.length);
            const visibleText = hasToolOrFinalAfter ? segment.content : displayedText.substring(textPosition, visibleEnd);

            if (visibleText && visibleText.trim().length > 0) {
              if (isWelcomeMessage) {
                result.push(
                  <div key={`text-${i}`} className="welcome-message">
                    <MarkdownText text={visibleText} />
                  </div>
                );
              } else {
                const hasToolAfter = segments.slice(i + 1).some(s => s.type === 'tool');
                const isThisSegmentStreaming = isLastMessage && isSending && visibleEnd < segmentEnd && !hasToolOrFinalAfter;
                const isLastTextSegment = !segments.slice(i + 1).some(s => s.type === 'text' || s.type === 'finalAnswer');
                const isStreamingLastText = isLastMessage && isSending && isLastTextSegment && !hasFinalAnswer && !hasToolAfter;
                const isHistoricalMessage = !(isLastMessage && isSending);

                // Pour les messages historiques sans [to user], afficher directement le contenu
                if (isHistoricalMessage && !hasFinalAnswer) {
                  result.push(
                    <div key={`final-${i}`} className="final-answer-box">
                      <div className="final-answer-content">
                        <MarkdownText text={visibleText} />
                      </div>
                    </div>
                  );
                } else {
                  result.push(
                    <ReflectionDropdown
                      key={`reflection-${i}`}
                      text={visibleText}
                      isStreaming={isThisSegmentStreaming || isStreamingLastText}
                    />
                  );
                }
              }
            }
          }

          textPosition = segmentEnd;

        } else if (segment.type === 'finalAnswer' && segment.content) {
          const segmentLength = segment.content.length;
          const segmentEnd = textPosition + segmentLength;

          if (textPosition < displayedText.length) {
            const visibleStart = textPosition;
            const visibleEnd = Math.min(segmentEnd, displayedText.length);
            const visibleText = displayedText.substring(visibleStart, visibleEnd);

            if (visibleText) {
              result.push(
                <div key={`final-${i}`} className="final-answer-box">
                  <div className="final-answer-content">
                    <MarkdownText text={visibleText} />
                  </div>
                </div>
              );
            }
          }

          textPosition = segmentEnd;

        } else if (segment.type === 'tool' && segment.tool) {
          if (textPosition <= displayedText.length || textPosition === 0) {
            const tool = segment.tool;
            const hasResult = tool.status !== undefined;
            const isSuccess = hasResult && (tool.status === 'success' || tool.status === 'true');
            const hasError = hasResult && !isSuccess;

            result.push(
              <div key={`tool-${i}`} className="tool-call-box">
                <div className="tool-call-header">
                  <span className="tool-name"><MarkdownText text={tool.name} /></span>
                </div>
                <div className="tool-call-result">
                  {!hasResult && <span className="tool-waiting">En attente du resultat...</span>}
                  {hasError && <span className="tool-error">L'outil n'a pas reussi</span>}
                  {isSuccess && <span className="tool-success"><MarkdownText text={tool.result || ''} /></span>}
                </div>
              </div>
            );
          }
        }
      }

      return result;
    };

    const hasReceivedFinalAnswer = text.includes('[to user]');
    const showReflectionPlaceholder = isLastMessage && isSending && !hasReceivedFinalAnswer;

    return (
      <div className="bot-text">
        {renderSegments()}
        {showReflectionPlaceholder && (
          <div className="reflection-waiting-placeholder">
            <span className="reflection-label shimmer">{thinkingMessage || 'Réflexion...'}</span>
          </div>
        )}
        {generatedContract && (
          <ContractDownloadBox contract={generatedContract} />
        )}
      </div>
    );
  };

// Allowed file types for the file input (PDF and DOCX only, max 10MB)
const ACCEPTED_FILE_TYPES = '.pdf,.docx';

// Component for displaying attached file chips
const AttachedFileChip: React.FC<{
  file: AttachedFile;
  onRemove: (id: string) => void;
  disabled?: boolean;
}> = ({ file, onRemove, disabled }) => {
  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <path d="M9 15h6" />
            <path d="M9 11h6" />
          </svg>
        );
      case 'docx':
      default:
        return (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        );
    }
  };

  const truncateName = (name: string, maxLength: number = 20) => {
    if (name.length <= maxLength) return name;
    const ext = name.split('.').pop() || '';
    const baseName = name.slice(0, name.length - ext.length - 1);
    const truncatedBase = baseName.slice(0, maxLength - ext.length - 4) + '...';
    return `${truncatedBase}.${ext}`;
  };

  return (
    <div className={`attached-file-chip ${file.status}`}>
      <span className="file-icon">{getFileIcon(file.name)}</span>
      <span className="file-name" title={file.name}>{truncateName(file.name)}</span>
      {file.status === 'uploading' && (
        <span className="file-progress">{file.progress}%</span>
      )}
      {file.status === 'error' && (
        <span className="file-error" title={file.error}>!</span>
      )}
      <button
        className="file-remove"
        onClick={() => onRemove(file.id)}
        disabled={disabled || file.status === 'uploading'}
        title="Supprimer"
        type="button"
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

const ChatbotPanel: React.FC = () => {
  const {
    textToAddToChatbot,
    setActivePage,
    setInitialContractId
  } = useAppContext();

  const { authStatus, microsoftDisplayName } = useAuth();
  const { activeAlias } = useImpersonation();

  const [input, setInput] = React.useState('');
  const [isHistoryOpen, setIsHistoryOpen] = React.useState(false);
  const [conversations, setConversations] = React.useState<api.Conversation[]>([]);
  const [searchQuery, setSearchQuery] = React.useState('');

  // State for Create Contract Section
  const [isCreateContractOpen, setIsCreateContractOpen] = React.useState(false);
  const [availableContracts, setAvailableContracts] = React.useState<api.ContractListItem[]>([]);
  const [isContractSearchOpen, setIsContractSearchOpen] = React.useState(false);
  const [contractSearchQuery, setContractSearchQuery] = React.useState('');

  // State for Deep Web Search
  const [isWebSearchActive, setIsWebSearchActive] = React.useState(false);

  // File attachments hook
  const {
    attachedFiles,
    addFiles,
    removeFile,
    uploadFiles,
    clearFiles,
    isUploading,
    hasFiles
  } = useFileAttachments();

  const listRef = React.useRef<HTMLDivElement>(null);
  const historyRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Use the chat hook
  const {
    messages,
    isSending,
    isWaitingFirstEvent,
    thinkingMessage,
    conversationId,
    conversationName,
    error,
    setError,
    sendMessage,
    clearMessages,
    loadConversation,
    cancelGeneration
  } = useChat();

  // Fetch contracts list
  React.useEffect(() => {
    const fetchContracts = async () => {
      try {
        const response = await api.getContracts();
        if (response.success && response.data) {
          setAvailableContracts(response.data);
        }
      } catch (err) {
        logger.error('Failed to load contracts:', err);
      }
    };
    fetchContracts();
  }, [activeAlias]);

  // Reset chat state when impersonation changes
  React.useEffect(() => {
    setConversations([]);
    setIsHistoryOpen(false);
    clearMessages();
  }, [activeAlias, clearMessages]);

  // Scroll adaptatif
  useAdaptiveChatScroll(listRef, messages, isSending, { thresholdPx: 20 });

  const adjustTextareaHeight = React.useCallback(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';

      const maxHeight = Math.min(500, window.innerHeight * 0.7);
      const minHeight = 20; // Garantit que le textarea reste visible et cliquable
      const newHeight = Math.max(minHeight, textarea.scrollHeight);

      if (newHeight <= maxHeight) {
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY = 'hidden';
      } else {
        textarea.style.height = `${maxHeight}px`;
        textarea.style.overflowY = 'auto';
      }
    }
  }, []);

  const handleTextareaKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const form = e.currentTarget.closest('form');
      if (form) {
        form.requestSubmit();
      }
    }
  }, []);

  React.useEffect(() => {
    adjustTextareaHeight();
  }, [input, adjustTextareaHeight]);

  React.useEffect(() => {
    const handleResize = () => {
      adjustTextareaHeight();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [adjustTextareaHeight]);

  React.useEffect(() => {
    if (textToAddToChatbot) {
      setInput(prevInput => {
        const newInput = prevInput ? `${prevInput} ${textToAddToChatbot}` : textToAddToChatbot;
        return newInput;
      });

      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const textLength = textareaRef.current.value.length;
          textareaRef.current.setSelectionRange(textLength, textLength);
        }
      }, 100);
    }
  }, [textToAddToChatbot]);

  // Detect document references in bot messages (web version - logging only)
  React.useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'bot') {
        detectDocumentInResponse(lastMessage.content);
      }
    }
  }, [messages]);

  // Close history dropdown on outside click
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        historyRef.current &&
        !historyRef.current.contains(event.target as Node)
      ) {
        setIsHistoryOpen(false);
      }
    };

    if (isHistoryOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isHistoryOpen]);

  // Recalculate dropdown width on resize
  React.useEffect(() => {
    const handleResize = () => {
      if (isHistoryOpen) {
        calculateDropdownWidth();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isHistoryOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || isSending || isUploading) return;

    // Capture file infos snapshot before any async work
    const hadFiles = attachedFiles.length > 0;
    const fileInfos: AttachedFileInfo[] = attachedFiles.map(f => ({
      name: f.name,
      size: f.file.size
    }));

    try {
      // Upload files first if any — failure here preserves input for retry
      let fileKeys: string[] = [];
      if (hadFiles) {
        fileKeys = await uploadFiles();
        logger.log('Files uploaded, keys:', fileKeys);
      }

      // Build options
      const options: {
        deep_web_search?: boolean;
        attachedFileKeys?: string[];
        attachedFileInfos?: AttachedFileInfo[];
      } = {};
      if (isWebSearchActive) {
        options.deep_web_search = true;
      }
      if (fileKeys.length > 0) {
        options.attachedFileKeys = fileKeys;
        options.attachedFileInfos = fileInfos;
      }

      // Upload succeeded — clear input and files before sending
      // so they disappear from the composer immediately
      setInput('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      clearFiles();

      // sendMessage adds the user message to chat synchronously,
      // then streams the bot response
      await sendMessage(trimmed, Object.keys(options).length > 0 ? options : undefined);
    } catch (error) {
      logger.error('Error sending message:', error);
      // Restore input so the user can retry
      setInput(trimmed);
    }
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      addFiles(files);
    }
    // Reset input to allow selecting same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Open file picker
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleNewConversation = () => {
    clearMessages();
    setIsWebSearchActive(false);
    clearFiles();
  };

  // Handler for contract click in Create section
  const handleContractClick = async (contract: api.ContractListItem) => {
    if (isSending || isUploading) return;

    const message = `Je veux créer un contrat : **${contract.name}**`;
    setIsCreateContractOpen(false);

    // Capture file infos and snapshot before clearing
    const hadFiles = attachedFiles.length > 0;
    const fileInfos: AttachedFileInfo[] = attachedFiles.map(f => ({
      name: f.name,
      size: f.file.size
    }));

    try {
      // Upload files first if any
      let fileKeys: string[] = [];
      if (hadFiles) {
        fileKeys = await uploadFiles();
        logger.log('Files uploaded for contract creation, keys:', fileKeys);
      }

      // Build options
      const options: {
        building_contract_id: number;
        attachedFileKeys?: string[];
        attachedFileInfos?: AttachedFileInfo[];
      } = {
        building_contract_id: contract.id
      };

      if (fileKeys.length > 0) {
        options.attachedFileKeys = fileKeys;
        options.attachedFileInfos = fileInfos;
      }

      // Upload succeeded — clear files from composer immediately
      clearFiles();

      await sendMessage(message, options);
    } catch (error) {
      logger.error('Error sending message:', error);
    }
  };

  // Load conversations
  const loadConversations = React.useCallback(async () => {
    try {
      const response = await api.listConversations(1, 100);
      if (response.success && response.data) {
        setConversations(response.data.items);
      } else {
        logger.error('Error loading conversations:', response.error);
        setConversations([]);
      }
    } catch (error) {
      logger.error('Error loading conversations:', error);
      setConversations([]);
    }
  }, []);

  // Format time for conversation display
  const formatTime = React.useCallback((conversation: api.Conversation): string => {
    if (conversation.id === conversationId) {
      return "actuelle";
    }

    const now = new Date();
    const conversationDate = new Date(conversation.created_at);
    const diffMs = now.getTime() - conversationDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours >= 24) {
      return '';
    }
    if (diffHours >= 1) {
      return `${diffHours}h`;
    }
    return `${diffMinutes}m`;
  }, [conversationId]);

  // Filter conversations by search
  const filterConversations = React.useCallback((convs: api.Conversation[], query: string) => {
    if (!query.trim()) return convs;

    const lowercaseQuery = query.toLowerCase();
    return convs.filter(conv =>
      conv.name.toLowerCase().includes(lowercaseQuery)
    );
  }, []);

  // Group conversations by date
  const groupConversationsByDate = React.useCallback((conversations: api.Conversation[]) => {
    const groups: { label: string; conversations: api.Conversation[] }[] = [];
    const groupMap: Record<string, api.Conversation[]> = {};

    const now = new Date();

    const getDaysDiff = (date: Date): number => {
      const dateStr = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' }).format(date);
      const nowStr = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' }).format(now);

      const dateInParis = new Date(dateStr);
      const nowInParis = new Date(nowStr);

      const diffTime = nowInParis.getTime() - dateInParis.getTime();
      return Math.round(diffTime / (1000 * 3600 * 24));
    };

    conversations.forEach(conv => {
      const conversationDate = new Date(conv.created_at);
      const diffDays = getDaysDiff(conversationDate);

      let label: string;
      if (diffDays === 0) {
        label = 'Aujourd\'hui';
      } else if (diffDays === 1) {
        label = 'Hier';
      } else if (diffDays < 7) {
        label = `Il y a ${diffDays} jours`;
      } else {
        label = 'Plus ancien';
      }

      if (!groupMap[label]) {
        groupMap[label] = [];
      }
      groupMap[label].push(conv);
    });

    const labelOrder = ['Aujourd\'hui', 'Hier'];
    const sortedLabels = Object.keys(groupMap).sort((a, b) => {
      const indexA = labelOrder.indexOf(a);
      const indexB = labelOrder.indexOf(b);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      if (a.startsWith('Il y a') && b.startsWith('Il y a')) {
        return parseInt(a.split(' ')[2]) - parseInt(b.split(' ')[2]);
      }
      if (a.startsWith('Il y a')) return -1;
      if (b.startsWith('Il y a')) return 1;

      return a === 'Plus ancien' ? 1 : -1;
    });

    sortedLabels.forEach(label => {
      groups.push({ label, conversations: groupMap[label] });
    });

    return groups;
  }, []);

  // Select a conversation
  const selectConversation = async (id: string, name: string) => {
    loadConversation(id, name);
    setIsHistoryOpen(false);
  };

  // Delete a conversation
  const deleteConversation = async (id: string) => {
    try {
      const response = await api.deleteConversation(id);

      if (!response.success) {
        throw new Error(response.error);
      }

      // Reload conversations after deletion
      await loadConversations();

      // If it was the active conversation, start a new one
      if (id === conversationId) {
        clearMessages();
      }
    } catch (error: any) {
      logger.error('Error deleting conversation:', error);
      setError(error.message || 'Failed to delete conversation');
    }
  };

  // Toggle history
  const toggleHistory = () => {
    setIsHistoryOpen(!isHistoryOpen);
    if (!isHistoryOpen) {
      loadConversations();
      setTimeout(calculateDropdownWidth, 0);
    } else {
      setSearchQuery('');
    }
  };

  // Calculate dropdown width
  const calculateDropdownWidth = () => {
    const chatbotPanel = document.querySelector('.chatbot-panel');
    if (chatbotPanel) {
      const panelWidth = chatbotPanel.clientWidth;
      const dropdownWidth = Math.floor(panelWidth * 0.9);
      document.documentElement.style.setProperty('--dropdown-width', `${dropdownWidth}px`);
    }
  };

  // Display welcome message if no messages yet
  const getFirstName = (): string => {
    // Priority 1: Microsoft Graph displayName (first word)
    if (microsoftDisplayName && microsoftDisplayName.trim()) {
      return microsoftDisplayName.split(' ')[0];
    }
    // Fallback: Extract from email
    const email = authStatus?.user?.email || '';
    const emailName = email.split('@')[0];
    const parts = emailName.split('.');
    if (parts.length > 0) {
      return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    }
    return '';
  };
  const firstName = getFirstName();
  const welcomeMessage = firstName
    ? `Bonjour ${firstName}\nComment puis-je vous aider aujourd'hui ?`
    : "Bonjour\nComment puis-je vous aider aujourd'hui ?";

  const displayMessages = messages;

  const isEmpty = messages.length === 0;

  return (
    <div className={`chatbot-panel ${isEmpty ? 'empty-mode' : ''}`}>
      <div className="chatbot-header">
        <div className="conversation-title">{conversationName || ''}</div>
        <div className="header-actions">
          <button
            className="header-btn"
            type="button"
            title="Nouvelle conversation"
            onClick={handleNewConversation}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
          <div className="history-container" ref={historyRef}>
            <button
              className={`header-btn ${isHistoryOpen ? 'active' : ''}`}
              type="button"
              title="Historique des conversations"
              onClick={toggleHistory}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
                <path d="M12 7v5l4 2" />
              </svg>
            </button>

            {isHistoryOpen && (
              <div className="history-dropdown">
                <div className="history-search">
                  <input
                    type="text"
                    placeholder="Search..."
                    className="history-search-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                {conversations.length === 0 ? (
                  <div className="history-empty">
                    <p>Aucune conversation</p>
                  </div>
                ) : (
                  <div className="history-sections">
                    {groupConversationsByDate(filterConversations(conversations, searchQuery)).map((section) => (
                      <div key={section.label} className="history-section">
                        <div className="history-section-header">{section.label}</div>
                        {section.conversations.map((conversation) => (
                          <div
                            key={conversation.id}
                            className={`history-item ${conversation.id === conversationId ? 'active' : ''}`}
                            onClick={() => selectConversation(conversation.id, conversation.name)}
                          >
                            <div className="history-item-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                              </svg>
                            </div>
                            <div className="history-item-content">
                              <div className="history-item-name">{conversation.name}</div>
                              <div className="history-item-time">{formatTime(conversation)}</div>
                            </div>
                            <button
                              className="history-item-delete"
                              title="Supprimer la conversation"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteConversation(conversation.id);
                              }}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18" />
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="chat-error-banner">
          Erreur: {error}
        </div>
      )}

      <div className="chat-content-wrapper">
        {isEmpty ? (
          <div className="welcome-hero">
            <h1 className="welcome-title">
              {firstName ? `Bonjour ${firstName}` : 'Bonjour'}
            </h1>
            <p className="welcome-subtitle">Comment puis-je vous aider aujourd'hui ?</p>
          </div>
        ) : (
          <div className="chat-messages" ref={listRef}>
            {displayMessages.map((msg, index) => {
              const isLastMessage = index === displayMessages.length - 1;
              return (
                <div key={msg.id} className={`chat-message ${msg.role}`}>
                  {msg.role === 'user' ? (
                    <div className="bubble">
                      <MarkdownText text={msg.content} />
                      {msg.attachedFiles && msg.attachedFiles.length > 0 && (
                        <div className="message-attachments">
                          {msg.attachedFiles.map((file, idx) => {
                            const ext = file.name.split('.').pop()?.toLowerCase();
                            const isPdf = ext === 'pdf';
                            return (
                              <div key={idx} className={`message-attachment-chip ${isPdf ? 'pdf' : 'docx'}`}>
                                <span className="attachment-icon-badge">
                                  {isPdf ? (
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                      <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                  ) : (
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                      <polyline points="14 2 14 8 20 8" />
                                      <line x1="16" y1="13" x2="8" y2="13" />
                                      <line x1="16" y1="17" x2="8" y2="17" />
                                    </svg>
                                  )}
                                </span>
                                <span className="attachment-name">{file.name}</span>
                                <span className="attachment-ext">{ext?.toUpperCase()}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <BotMessage
                      text={msg.content}
                      isLastMessage={isLastMessage}
                      isSending={isSending}
                      isWelcomeMessage={false}
                      toolCalls={msg.toolCalls}
                      streamSegments={msg.streamSegments}
                      isWaitingFirstEvent={isWaitingFirstEvent}
                      thinkingMessage={isLastMessage ? thinkingMessage : null}
                      generatedContract={msg.generatedContract}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <form className="composer" onSubmit={handleSubmit}>
        <div className="input-container">
          {isCreateContractOpen && (
            <div className="create-contract-section">
              <div className="create-contract-header">
                <div className="create-contract-title">Créer un nouveau contrat</div>
                <div className="create-contract-search-container">
                  {isContractSearchOpen ? (
                    <div className="contract-search-box">
                      <input
                        type="text"
                        className="contract-search-input"
                        placeholder="Rechercher..."
                        value={contractSearchQuery}
                        onChange={(e) => setContractSearchQuery(e.target.value)}
                        autoFocus
                        onBlur={() => {
                          if (!contractSearchQuery) setIsContractSearchOpen(false);
                        }}
                      />
                      <button
                        className="contract-search-close"
                        onClick={() => {
                          setIsContractSearchOpen(false);
                          setContractSearchQuery('');
                        }}
                      >
                        x
                      </button>
                    </div>
                  ) : (
                    <button
                      className="contract-search-btn"
                      onClick={() => setIsContractSearchOpen(true)}
                      title="Rechercher"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              <div className="create-contract-list">
                {availableContracts
                  .filter(c => c.name.toLowerCase().includes(contractSearchQuery.toLowerCase()))
                  .map((contract) => (
                    <div key={contract.id} className="contract-selection-item">
                      <button
                        type="button"
                        className="contract-name-button"
                        onClick={() => handleContractClick(contract)}
                      >
                        {contract.name}
                      </button>
                      <button
                        type="button"
                        className="contract-info-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setInitialContractId(contract.id);
                          setActivePage('page4');
                        }}
                        title="Voir les details"
                        aria-label={`Voir les details du contrat ${contract.name}`}
                      >
                        i
                      </button>
                    </div>
                  ))}
                {availableContracts.filter(c => c.name.toLowerCase().includes(contractSearchQuery.toLowerCase())).length === 0 && (
                  <div className="contract-list-empty">Aucun resultat</div>
                )}
              </div>
            </div>
          )}
          {/* Attached files display */}
          {hasFiles && (
            <div className="attached-files">
              {attachedFiles.map((file) => (
                <AttachedFileChip
                  key={file.id}
                  file={file}
                  onRemove={removeFile}
                  disabled={isSending || isUploading}
                />
              ))}
            </div>
          )}
          <div className="input-field">
            <textarea
              ref={textareaRef}
              placeholder="Chercher, ajouter, reformuler..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleTextareaKeyDown}
              aria-label="Message"
              rows={1}
            />
          </div>
          <div className="input-footer">
            <div className="action-buttons">
              <button
                className={`action-btn ${hasFiles ? 'active' : ''}`}
                type="button"
                title="Joindre un fichier"
                onClick={openFilePicker}
                disabled={isSending || isUploading}
              >
                <span className="action-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                  </svg>
                </span>
                <span className="action-text">Joindre</span>
              </button>
              <button
                className={`action-btn ${isCreateContractOpen ? 'active' : ''}`}
                type="button"
                title="Créer un contrat"
                onClick={() => {
                  setIsCreateContractOpen(!isCreateContractOpen);
                  if (!isCreateContractOpen) setIsWebSearchActive(false);
                }}
              >
                <span className="action-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </span>
                <span className="action-text">Créer</span>
              </button>
              <button
                className={`action-btn ${isWebSearchActive ? 'active' : ''}`}
                type="button"
                title="Recherche web approfondie"
                onClick={() => {
                  setIsWebSearchActive(!isWebSearchActive);
                  if (!isWebSearchActive) setIsCreateContractOpen(false);
                }}
              >
                <span className="action-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                  </svg>
                </span>
                <span className="action-text">Recherche</span>
              </button>
            </div>
            {isSending ? (
              <button
                className="send-btn stop-btn"
                type="button"
                onClick={cancelGeneration}
                title="Arrêter la génération"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="4" y="4" width="16" height="16" rx="2" />
                </svg>
              </button>
            ) : (
              <button className="send-btn" type="submit" disabled={!input.trim()} title="Envoyer">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 2L11 13" />
                  <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </form>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        multiple
        accept={ACCEPTED_FILE_TYPES}
        onChange={handleFileSelect}
      />
    </div>
  );
};

export { MarkdownText };
export default ChatbotPanel;
